import { numberFormatter } from "@opensea/i18n"
import { BigNumber, bn } from "@opensea/utils"
import { isAddress } from "viem"
import { z } from "zod"
import { MINIMUM_OFFER_PRICE } from "../../flow/BottomSheetFlows/constants"
import { expirationSchema } from "./expiration"

const format = numberFormatter()

export const evmAddressSchema = z
  .string()
  // Wrapping boolean as we don't want to actually coerce the type to an Address type
  .refine(value => Boolean(isAddress(value)), "Invalid address.")

// Disallow TLDs for sanctioned countries
const DISALLOWED_TLDS = ["cu", "ir", "sy", "kp"]

export const emailSchema = z
  .string()
  .email("Please enter a valid email.")
  .refine(value => {
    return !DISALLOWED_TLDS.some(tld => value.endsWith(`.${tld}`))
  }, "Email domain is not supported.")

export const orderSchema = z.object({
  id: z.string(),
  price: z.float64(),
  quantity: z
    .int({
      error: issue =>
        issue.input === undefined
          ? "Please enter a valid quantity"
          : "Please enter a valid quantity",
    })
    .positive(),
  creatorFeeBasisPoints: z.int(),
  openSeaFeeBasisPoints: z.int(),
  isCreatorFeeRequired: z.boolean(),
  applyCreatorFee: z.boolean(),
  floorPrice: z.float64().optional(),
  bestOffer: z.float64().optional(),
  shouldConfirmPrice: z.boolean().optional(),
})
export const getOfferPricePrecision = (price: number) => {
  if (price > 1) {
    return 2
  }
  if (price > 0.1) {
    return 3
  }
  return 4
}
// Shared validation helpers for offer schemas
const validateMaxQuantity = (
  { maxQuantity, quantity }: { maxQuantity?: number; quantity: number },
  ctx: z.RefinementCtx,
) => {
  if (maxQuantity && quantity > maxQuantity) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `You can only offer on up to ${maxQuantity} items.`,
      path: ["quantity"],
    })
  }
}

const validateTokenBalance = (
  data: {
    tokenBalance?: BigNumber
    price: number
    quantity: number
    offerSymbol: string
    nativeCurrencySymbol: string
    isWrappedNativeCurrency?: boolean
  },
  ctx: z.RefinementCtx,
) => {
  const {
    tokenBalance,
    price,
    quantity,
    offerSymbol,
    nativeCurrencySymbol,
    isWrappedNativeCurrency,
  } = data
  const totalOfferAmount = quantity ? price * quantity : price

  if (tokenBalance && !tokenBalance.isGreaterThanOrEqualTo(totalOfferAmount)) {
    const suffix = isWrappedNativeCurrency
      ? `${offerSymbol}/${nativeCurrencySymbol}`
      : offerSymbol
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Offer exceeds wallet balance by ${format(bn(totalOfferAmount).minus(bn(tokenBalance)), { display: "standard", suffix })}`,
      path: ["price"],
    })
  }
}

const validateMinimumUsdValue = (
  {
    price,
    minimumOfferValueUsd,
    currencyUsdValue,
  }: {
    price: number
    minimumOfferValueUsd?: BigNumber
    currencyUsdValue?: BigNumber
  },
  ctx: z.RefinementCtx,
) => {
  const offerUsdValue = currencyUsdValue ? currencyUsdValue.times(price) : bn(0)
  const isValid = minimumOfferValueUsd
    ? offerUsdValue.isGreaterThanOrEqualTo(minimumOfferValueUsd)
    : true

  if (!isValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Offer must be at least ${format(minimumOfferValueUsd, { display: "standard", suffix: "USD" })}`,
      path: ["price"],
    })
  }
}

const offerSchema = orderSchema
  .safeExtend({
    tokenBalance: z.instanceof(BigNumber).optional(),
    minimumOfferValueUsd: z.instanceof(BigNumber).optional(),
    currencyUsdValue: z.instanceof(BigNumber).optional(),
    offerSymbol: z.string(),
    nativeCurrencySymbol: z.string(),
    isWrappedNativeCurrency: z.boolean().optional(),
    maxQuantity: z.int().optional(),
    price: z
      .float64({
        error: () => "",
      })
      .gte(
        MINIMUM_OFFER_PRICE,
        `Offer amount must be at least ${MINIMUM_OFFER_PRICE} ETH`,
      )
      .superRefine((price, ctx) => {
        const decimalPlaces = price.toString().split(".")[1]?.length || 0
        const maxAllowedDecimals = getOfferPricePrecision(price)
        if (decimalPlaces > maxAllowedDecimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Maximum ${maxAllowedDecimals} decimal places allowed`,
          })
        }
      }),
  })
  .superRefine(validateMaxQuantity)
  .superRefine(validateTokenBalance)
  .superRefine(validateMinimumUsdValue)

const collectionOfferSchema = offerSchema
  .safeExtend({
    trait: z.optional(
      z.object({
        traitType: z.string(),
        value: z.string(),
      }),
    ),
    traits: z.optional(
      z.array(
        z.object({
          traitType: z.string(),
          value: z.string(),
        }),
      ),
    ),
    traitFloorPrice: z.float64().optional(),
    traitTopOfferPrice: z.float64().optional(),
  })
  .superRefine(validateMaxQuantity)
  .superRefine(validateTokenBalance)
  .superRefine(validateMinimumUsdValue)

export const offerFlowSchema = z.object({
  expiration: expirationSchema,
  offers: z.array(offerSchema),
})

export const collectionOfferFlowSchema = z.object({
  expiration: expirationSchema,
  offers: z.array(collectionOfferSchema),
})
